import { jsx as _jsx } from "react/jsx-runtime";
import '../../css/holiday-picker.css';
import { Box } from '@mui/material';
import { addMonths, addYears, format, isEqual } from 'date-fns';
import moment from 'moment';
import { useCallback, useContext, useEffect, useState } from 'react';
import DatePicker from 'react-datepicker';
import { isBreakpointMobile, useBreakpoint } from '../../breakpoint';
import { checkIfWorkingDay, translateStatusToClassName } from '../../functions';
import { ReducerContext } from '../../hooks/reducer/reducerContext';
import { CalendarContext } from '../calendarContext';
import { NewBookingContext } from '../NewBookingContext';
export function HolidayPicker() {
    const breakpoint = useBreakpoint();
    const isMobile = isBreakpointMobile(breakpoint);
    const { freeDays } = useContext(CalendarContext);
    const { dateRange, startInAfternoon, endInMorning, updateState } = useContext(NewBookingContext);
    const { modalState, holidayState, userSettingsState } = useContext(ReducerContext);
    const [startDate, setStartDate] = useState(dateRange.startDate?.toDate() ?? undefined);
    const [months, setMonths] = useState(isMobile ? 3 : 6);
    const [endDate, setEndDate] = useState(dateRange?.endDate?.toDate() ?? null);
    const [intialVisibleMonth, setInitialVisibleMonth] = useState();
    const onChange = useCallback((dates) => {
        const [start, end] = dates;
        setStartDate(start);
        setEndDate(end);
        const inThePast = (end && end < new Date()) || (end === undefined && start && start < new Date());
        const outsideOfCurrentYear = end && end < addYears(new Date(userSettingsState.end), -1);
        updateState((prevState) => ({
            ...prevState,
            dateRange: { startDate: moment(start), endDate: end ? moment(end) : null },
            deductFromEntitlement: !inThePast || outsideOfCurrentYear,
        }));
    }, [updateState, userSettingsState.end]);
    const getDayClassName = useCallback((date) => {
        const datePart = format(date, 'yyyy-MM-dd');
        const currentDate = new Date(date).setHours(0, 0, 0, 0);
        let className = '';
        if (holidayState.holidays.map((x) => format(x, 'yyyy-MM-dd')).includes(datePart)) {
            let upcoming = false;
            const bookings = holidayState.holidayBookings.filter((x) => new Date(x.startDate).setHours(0, 0, 0, 0) <= currentDate &&
                new Date(x.endDate).setHours(0, 0, 0, 0) >= currentDate);
            const latestBooking = bookings.sort((a, b) => a.timeAdded > b.timeAdded ? 1 : -1)[bookings.length - 1];
            if (latestBooking) {
                upcoming =
                    new Date(latestBooking.endDate).setHours(0, 0, 0, 0) >
                        new Date().setHours(0, 0, 0, 0);
                className = translateStatusToClassName(latestBooking.approvalStatus, upcoming);
                if (latestBooking.isStartHalfDay &&
                    isEqual(currentDate, new Date(latestBooking.startDate).setHours(0, 0, 0, 0))) {
                    className += '_start-morning';
                }
                if (latestBooking.isEndHalfDay &&
                    isEqual(currentDate, new Date(latestBooking.endDate).setHours(0, 0, 0, 0))) {
                    className += '_end-afternoon';
                }
            }
        }
        const isStartOfSelection = startDate && isEqual(startDate, date);
        const isEndOfSelection = endDate && isEqual(endDate, date);
        const isFreeDay = freeDays && freeDays.find((em) => em === datePart);
        // single day selected
        if (isStartOfSelection && isEndOfSelection) {
            if (startInAfternoon) {
                className = 'day_selected_start-morning';
            }
            else if (endInMorning) {
                className = 'day_selected_end-afternoon';
            }
        }
        // multi day selected
        if (startDate && endDate) {
            if (startInAfternoon && isStartOfSelection) {
                className = 'day_selected_start-morning';
            }
            else if (endInMorning && isEndOfSelection) {
                className = 'day_selected_end-afternoon';
            }
        }
        if (!checkIfWorkingDay(date, userSettingsState.workingDays)) {
            className = 'day_weekend';
            if (startInAfternoon && isStartOfSelection) {
                className += '_start-morning';
            }
            else if (endInMorning &&
                (isEndOfSelection || (!endDate && isStartOfSelection))) {
                className += '_end-afternoon';
            }
        }
        if (isFreeDay) {
            className = 'day_free_day';
            if (startInAfternoon && isStartOfSelection) {
                className += '_start-morning';
            }
            else if (endInMorning &&
                (isEndOfSelection || (!endDate && isStartOfSelection))) {
                className += '_end-afternoon';
            }
        }
        return className;
    }, [
        holidayState.holidays,
        holidayState.holidayBookings,
        startDate,
        endDate,
        freeDays,
        userSettingsState.workingDays,
        startInAfternoon,
        endInMorning,
    ]);
    let overlayClass = '';
    if (startInAfternoon) {
        overlayClass += ' start-in-afternoon';
    }
    if (endInMorning) {
        if (dateRange.endDate) {
            overlayClass += ' end-in-morning';
        }
        else {
            overlayClass += ' one-day-morning-only';
        }
    }
    useEffect(() => {
        const month = modalState.addBookingModalDate?.toDate() ?? new Date();
        setInitialVisibleMonth(month);
    }, [modalState.addBookingModalDate]);
    const calendarElement = document.getElementsByClassName('react-datepicker')[0];
    // I can't figure out the correct type for event here
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const onScroll = useCallback((event) => {
        const isAtBottom = event.target.scrollHeight - event.srcElement.scrollTop <=
            event.srcElement.offsetHeight;
        const isAtTop = event.target.scrollTop === 0;
        if (isAtBottom) {
            setMonths((p) => p + 2);
            setInitialVisibleMonth((p) => (p !== undefined ? addMonths(p, 2) : p));
        }
        if (isAtTop) {
            setMonths((p) => p + 2);
            setInitialVisibleMonth((p) => (p !== undefined ? addMonths(p, -2) : p));
        }
    }, []);
    useEffect(() => {
        calendarElement?.addEventListener('scroll', onScroll);
        return () => calendarElement?.removeEventListener('scroll', onScroll);
    }, [calendarElement, onScroll]);
    return (_jsx(Box, { component: "div", className: overlayClass, height: '100%', children: _jsx(Box, { height: '100%', children: _jsx(DatePicker, { fixedHeight: false, onChange: onChange, startDate: startDate, endDate: endDate, selectsRange: true, monthsShown: months, calendarStartDay: 1, dayClassName: getDayClassName, showPreviousMonths: !isMobile, openToDate: isMobile
                    ? intialVisibleMonth
                    : moment(intialVisibleMonth).add(1, 'month').toDate(), inline: true }) }) }));
}
